import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { useStore } from '../store';
import { shared } from './shared';
import { synthesize } from '../api';

// The Harpy Eagle names & describes a creature when you fly close to it.
export const INFO = {
  jaguar: { name: 'Jaguar', text: 'The jaguar — the outrage predator. It prowls the clearings of consensus and pounces on any grazing agreement.' },
  lion: { name: 'Lion', text: 'The lion — the alpha partisan. It hunts in the open, chasing the moderate herds until they scatter.' },
  tiger: { name: 'Tiger', text: 'The tiger — the ambush ideologue. Striped for cover, it lunges at the grazers of the middle ground.' },
  bear: { name: 'Bear', text: 'The bear — the market itself. Slow, heavy and unpredictable, it lumbers through the interior on its own terms.' },
  donkey: { name: 'Donkey', text: 'The donkey — a grazing progressive, nibbling the leaf-cutter hives of the left bank and bolting at the first sign of a predator.' },
  elephant: { name: 'Elephant', text: 'The elephant — a grazing conservative, guarding the stone fortresses of the right bank with long memory and slow trust.' },
  caiman: { name: 'Caiman', text: 'The caiman — the lurking cynic. It basks at the water\'s edge, half-submerged between the two banks, watching everything.' },
  macaw: { name: 'Scarlet Macaw', text: 'The scarlet macaw — the pundit of the canopy, flashing bright colours and repeating whatever draws the eye.' },
  sloth: { name: 'Sloth', text: 'The sloth — the disengaged voter. It hangs from the canopy, moving so slowly the news cycle passes it by.' },
  anaconda: { name: 'Anaconda', text: 'The anaconda — the slow-tightening narrative, coiling quietly through the trees and constricting the story from the shadows.' },
  howler: { name: 'Howler Monkey', text: 'The howler monkey — pure amplification. Its call carries for miles, drowning nuance under sheer volume.' },
  piranha: { name: 'Piranha', text: 'The piranha — the pile-on. Alone it is harmless, but it swarms every contested artifact drifting down the river.' },
  dolphin: { name: 'River Dolphin', text: 'The river dolphin — the genuine independent, moving down the neutral channel aligned to no bank at all.' },
  otter: { name: 'River Otter', text: 'The river otter — the delighted contrarian, tumbling playfully through the current just to see what floats.' },
  shark: { name: 'Bull Shark', text: 'The bull shark — enforcer of the boundary. It patrols the line between the banks and snaps at anyone who drifts too close.' },
  whale: { name: 'Whale', text: 'The whale — a mega-donor. It moves slowly down the central channel, and when it breaches, everything nearby scatters in its wake.' },
  eel: { name: 'Electric Eel', text: 'The electric eel — an information broker. It darts along the banks in glowing bursts, carrying leaks and insider currents.' },
  goldfish: { name: 'Goldfish', text: 'The goldfish — the distracted public. It swarms the shallows in dense, restless schools, memory as short as the next headline.' },
};

const RADIUS = 16; // horizontal trigger distance
const SPECIES_COOLDOWN = 28; // seconds before the same species can be re-announced

export default function FieldGuide() {
  const busy = useRef(false);
  const acc = useRef(0);
  const audio = useRef(null);
  const cooldowns = useRef({}); // key -> last-narrated clock time
  const lastKey = useRef(null);

  useFrame((state, delta) => {
    acc.current += delta;
    if (acc.current < 0.3) return;
    acc.current = 0;
    if (!audio.current) audio.current = new Audio();

    const s = useStore.getState();
    // Guide must be 100% silent under Master Mute OR Mute Guide — enforce on the
    // element every frame (kills any in-flight clip) and never trigger while muted.
    const guideMuted = !s.audioEnabled || s.guideVolume < 0.01;
    audio.current.volume = guideMuted ? 0 : Math.max(0, Math.min(1, s.guideVolume));
    audio.current.muted = guideMuted;

    if (!s.entered || s.tourActive || s.narrating || busy.current || guideMuted) return;

    const cam = shared.camPos;
    const now = state.clock.elapsedTime;

    // Nearest identifiable creature (horizontal distance, so altitude doesn't matter)
    let near = null, best = RADIUS * RADIUS;
    for (const f of shared.fauna) {
      const dx = f.pos.x - cam.x, dz = f.pos.z - cam.z;
      const d = dx * dx + dz * dz;
      if (d < best) { best = d; near = f.key; }
    }

    if (!near) { lastKey.current = null; return; }
    if (near === lastKey.current) return;
    const last = cooldowns.current[near] || -999;
    if (now - last < SPECIES_COOLDOWN) return;

    const info = INFO[near];
    if (!info) return;

    lastKey.current = near;
    cooldowns.current[near] = now;
    busy.current = true;

    (async () => {
      const st = useStore.getState();
      try {
        st.setNarrating(true);
        st.setBankFocus(null);
        st.setFieldCard({ name: info.name, text: info.text });
        st.setCaption(info.text, `FIELD GUIDE · ${info.name.toUpperCase()}`);
        let url = null;
        try { url = await synthesize(info.text, 'sage'); } catch (e) { url = null; }
        await new Promise((res) => {
          const a = audio.current;
          const done = () => res();
          const to = setTimeout(done, 20000);
          a.onended = () => { clearTimeout(to); res(); };
          a.onerror = () => { clearTimeout(to); res(); };
          if (url) { a.src = url; a.play().catch(done); } else { setTimeout(done, Math.min(8000, 2500 + info.text.length * 45)); }
        });
      } catch (e) {
        /* ignore */
      } finally {
        st.setNarrating(false);
        st.setFieldCard(null);
        busy.current = false;
      }
    })();
  });

  return null;
}
